import { isSafeHttpOrHttpsUrl } from "@/lib/security/safe-href";

/** Query param on `/api/demo-embed` — absolute demo URL to load. */
export const DEMO_EMBED_URL_PARAM = "url";
/** Meta/body marker when the proxy could not fetch upstream HTML (client may fall back to direct iframe). */
export const DEMO_EMBED_FAILED_META_NAME = "techxeed-demo-embed-failed";
/** Narrow viewport for mobile preview frame (`width=390`). */
export const DEMO_EMBED_VIEWPORT_PARAM = "viewport";
/** Container width (px) used to scale desktop preview on narrow hosts (e.g. phones). */
export const DEMO_EMBED_FIT_WIDTH_PARAM = "fitWidth";
/** Desktop layout strategy: `frame` = 1280px iframe scaled by host; `meta` = viewport initial-scale. */
export const DEMO_EMBED_LAYOUT_PARAM = "layout";
export const DEMO_EMBED_LAYOUT_FRAME_VALUE = "frame";
export const DEMO_EMBED_LAYOUT_META_VALUE = "meta";
export const DEMO_EMBED_MOBILE_VIEWPORT_VALUE = "mobile";
export const DEMO_EMBED_DESKTOP_VIEWPORT_VALUE = "desktop";
/** Layout viewport width injected for desktop live preview (`width=1280`). */
export const DESKTOP_EMBED_LAYOUT_WIDTH = 1280;
export const DESKTOP_EMBED_LAYOUT_HEIGHT = 720;
/** Portrait layout for mobile live preview (`width=390`). */
export const MOBILE_EMBED_LAYOUT_WIDTH = 390;
export const MOBILE_EMBED_LAYOUT_HEIGHT = 700;
export type DemoEmbedViewportMode = "desktop" | "mobile";

export function computeDesktopEmbedScale(fitWidthPx?: number): number {
	if (fitWidthPx != null && fitWidthPx > 0 && fitWidthPx < DESKTOP_EMBED_LAYOUT_WIDTH) {
		return Math.round((fitWidthPx / DESKTOP_EMBED_LAYOUT_WIDTH) * 1000) / 1000;
	}
	return 1;
}

/**
 * Sandboxed demo embed. Omits `allow-top-navigation` so frame-busting scripts cannot redirect the parent page.
 */
export const DEMO_IFRAME_SANDBOX =
	"allow-scripts allow-forms allow-same-origin allow-modals allow-downloads";

/**
 * Client iframe `src` for a storefront live demo (same-origin proxy + in-frame navigation guard).
 */
export function getDemoEmbedProxySrc(
	demoUrl: string,
	options?: {
		viewport?: DemoEmbedViewportMode;
		fitWidth?: number;
		layout?: typeof DEMO_EMBED_LAYOUT_FRAME_VALUE | typeof DEMO_EMBED_LAYOUT_META_VALUE;
	},
): string {
	const trimmed = demoUrl.trim();
	const params = new URLSearchParams({ [DEMO_EMBED_URL_PARAM]: trimmed });
	const viewport =
		options?.viewport === "mobile"
			? DEMO_EMBED_MOBILE_VIEWPORT_VALUE
			: DEMO_EMBED_DESKTOP_VIEWPORT_VALUE;
	params.set(DEMO_EMBED_VIEWPORT_PARAM, viewport);
	if (viewport === DEMO_EMBED_DESKTOP_VIEWPORT_VALUE) {
		const layout = options?.layout ?? DEMO_EMBED_LAYOUT_FRAME_VALUE;
		params.set(DEMO_EMBED_LAYOUT_PARAM, layout);
		if (
			layout === DEMO_EMBED_LAYOUT_META_VALUE &&
			options?.fitWidth != null &&
			options.fitWidth > 0
		) {
			params.set(DEMO_EMBED_FIT_WIDTH_PARAM, String(Math.round(options.fitWidth)));
		}
	}
	return `/api/demo-embed?${params.toString()}`;
}

export function parseDemoEmbedTargetUrl(
	raw: string | null | undefined,
): { ok: true; url: URL } | { ok: false; status: number; error: string } {
	if (raw == null || typeof raw !== "string" || !raw.trim()) {
		return { ok: false, status: 400, error: "Missing demo URL" };
	}
	const trimmed = raw.trim();
	const normalized = /^(https?:)?\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
	if (!isSafeHttpOrHttpsUrl(normalized)) {
		return { ok: false, status: 400, error: "Invalid demo URL" };
	}
	let url: URL;
	try {
		url = new URL(normalized);
	} catch {
		return { ok: false, status: 400, error: "Invalid demo URL" };
	}
	if (process.env.NODE_ENV === "production" && isPrivateOrLocalHostname(url.hostname)) {
		return { ok: false, status: 403, error: "Demo host not allowed" };
	}
	return { ok: true, url };
}

function isPrivateOrLocalHostname(hostname: string): boolean {
	const h = hostname.toLowerCase();
	if (h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0" || h === "::1") return true;
	if (/^10\./.test(h) || /^192\.168\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h)) return true;
	return false;
}

export function escapeHtmlAttr(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

/** Same-origin failure document so the iframe client can detect proxy failure and fall back. */
export function buildDemoEmbedFailureHtml(message: string): string {
	const safe = message
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
	return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="${DEMO_EMBED_FAILED_META_NAME}" content="1"><title>Demo unavailable</title></head><body data-${DEMO_EMBED_FAILED_META_NAME}="1">${safe}</body></html>`;
}

/**
 * Injected into proxied demo HTML: block off-origin links, popups, and route same-origin navigations via the proxy.
 */
export function getDemoEmbedGuardScript(): string {
	return `(function(){
var meta=document.querySelector('meta[name="techxeed-demo-allowed-origin"]');
if(!meta)return;
var allowed=meta.getAttribute('content');
if(!allowed)return;
function demoBase(){
var b=document.querySelector('base[href]');
return b?b.href:allowed+'/';
}
function resolveUrl(href){
try{return new URL(href,demoBase());}catch(e){return null;}
}
function sameOrigin(u){return u&&u.origin===allowed;}
function embedViewportSuffix(){
	var s=window.location.search;
	var vp=(s.match(/[?&]${DEMO_EMBED_VIEWPORT_PARAM}=(mobile|desktop)/)||[])[1]||'${DEMO_EMBED_DESKTOP_VIEWPORT_VALUE}';
	var layout=(s.match(/[?&]${DEMO_EMBED_LAYOUT_PARAM}=(frame|meta)/)||[])[1]||'${DEMO_EMBED_LAYOUT_FRAME_VALUE}';
	var q='&${DEMO_EMBED_VIEWPORT_PARAM}='+encodeURIComponent(vp)+'&${DEMO_EMBED_LAYOUT_PARAM}='+encodeURIComponent(layout);
	var fw=(s.match(/[?&]${DEMO_EMBED_FIT_WIDTH_PARAM}=(\\d+)/)||[])[1];
	if(fw&&layout==='${DEMO_EMBED_LAYOUT_META_VALUE}')q+='&${DEMO_EMBED_FIT_WIDTH_PARAM}='+encodeURIComponent(fw);
	return q;
}
function toProxy(u){return'/api/demo-embed?${DEMO_EMBED_URL_PARAM}='+encodeURIComponent(u.href)+embedViewportSuffix();}
document.addEventListener('click',function(e){
var a=e.target&&e.target.closest?e.target.closest('a'):null;
if(!a)return;
var raw=a.getAttribute('href');
if(raw==null||raw===''||(raw.charAt(0)==='#'&&raw.length===1))return;
if(raw.charAt(0)==='#')return;
var u=resolveUrl(a.href);
if(!sameOrigin(u)){e.preventDefault();e.stopImmediatePropagation();return;}
e.preventDefault();e.stopImmediatePropagation();
var next=toProxy(u);
if(window.location.pathname+window.location.search!==next){window.location.assign(next);}
},true);
document.addEventListener('submit',function(e){
var form=e.target;
if(!form||form.tagName!=='FORM')return;
var action=form.getAttribute('action')||allowed;
var u=resolveUrl(action);
if(!sameOrigin(u)){e.preventDefault();e.stopImmediatePropagation();}
},true);
var open=window.open;
window.open=function(url){
if(typeof url==='string'&&url){var u=resolveUrl(url);if(!sameOrigin(u))return null;}
return open.apply(window,arguments);
};
function dismissStuckPreloaders(){
try{
if(window.__techxeedPreloaderDismissed)return;
window.__techxeedPreloaderDismissed=true;
var nodes=document.querySelectorAll('#page-loader-pro,.page-loader-pro,.qode-smooth-transition-loader,.mkdf-smooth-transition-loader,.site-preloader,#preloader,.preloader');
for(var i=0;i<nodes.length;i++){
var el=nodes[i];
if(el===document.documentElement||el===document.body)continue;
el.classList.add('finished-loading');
el.style.setProperty('display','none','important');
el.style.setProperty('opacity','0','important');
el.style.setProperty('visibility','hidden','important');
el.style.setProperty('pointer-events','none','important');
}
document.documentElement.style.setProperty('opacity','1','important');
document.documentElement.style.setProperty('visibility','visible','important');
if(document.body){
document.body.style.setProperty('opacity','1','important');
document.body.style.setProperty('visibility','visible','important');
document.body.classList.add('progression-preloader-completed');
}
var boxed=document.getElementById('boxed-layout-pro');
if(boxed){
boxed.classList.add('progression-preloader-completed');
boxed.style.setProperty('opacity','1','important');
boxed.style.setProperty('visibility','visible','important');
}
}catch(e){}
}
window.addEventListener('load',function(){setTimeout(dismissStuckPreloaders,1200);});
setTimeout(dismissStuckPreloaders,4500);
})();`;
}

export function injectDemoEmbedGuard(html: string, allowedOrigin: string, baseHref: string): string {
	const originAttr = escapeHtmlAttr(allowedOrigin);
	const baseAttr = escapeHtmlAttr(baseHref);
	const injection = `<meta name="techxeed-demo-allowed-origin" content="${originAttr}"><base href="${baseAttr}"><script>${getDemoEmbedGuardScript()}<\/script>`;
	if (/<head(\s[^>]*)?>/i.test(html)) {
		return html.replace(/<head(\s[^>]*)?>/i, (m) => `${m}${injection}`);
	}
	if (/<html(\s[^>]*)?>/i.test(html)) {
		return html.replace(/<html(\s[^>]*)?>/i, (m) => `${m}<head>${injection}</head>`);
	}
	return `<!DOCTYPE html><html><head>${injection}</head><body>${html}</body></html>`;
}

export const DEMO_EMBED_FETCH_HEADERS: Record<string, string> = {
	Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
	"Accept-Language": "en-US,en;q=0.9",
	"User-Agent":
		"Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36",
};

/**
 * CSP for the proxied demo HTML document only (served from /api/demo-embed).
 * Must allow external base + assets; the main site CSP uses base-uri 'self' and blocks those.
 */
export const DEMO_EMBED_DOCUMENT_CSP = [
	"default-src * 'unsafe-inline' 'unsafe-eval' data: blob:",
	"script-src * 'unsafe-inline' 'unsafe-eval'",
	"style-src * 'unsafe-inline' https: http:",
	"img-src * data: blob: https: http:",
	"font-src * data: https: http:",
	"connect-src * ws: wss: blob: https: http:",
	"frame-src https: http:",
	"object-src 'none'",
	"base-uri 'self' https: http:",
	"form-action https: http:",
].join("; ");

/** Site root on the demo host so relative asset paths (e.g. iframe-demo/assets/...) resolve correctly. */
export function getDemoEmbedBaseHref(pageUrl: URL): string {
	return `${pageUrl.origin}/`;
}

export function stripHtmlBaseTags(html: string): string {
	return html.replace(/<base\b[^>]*>/gi, "");
}

const RELATIVE_URL_ATTR_RE =
	/\b(href|src|action)=(["'])(?!(?:https?:|\/\/|#|mailto:|tel:|javascript:|data:))([^"']*)\2/gi;

/** Resolve relative asset URLs against the demo page so they load from the demo host, not our app origin. */
export function absolutizeDemoRelativeUrls(html: string, pageUrl: URL): string {
	return html.replace(RELATIVE_URL_ATTR_RE, (match, attr, quote, rel) => {
		try {
			const absolute = new URL(rel, pageUrl.href).href;
			return `${attr}=${quote}${absolute}${quote}`;
		} catch {
			return match;
		}
	});
}

export function getDesktopEmbedForcedLayoutScript(initialScale: number): string {
	const w = DESKTOP_EMBED_LAYOUT_WIDTH;
	const scale = initialScale > 0 ? initialScale : 1;
	return `(function(){
var W=${w};
var SCALE=${scale};
var UA="Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36";
function viewportContent(){return"width="+W+", initial-scale="+SCALE+", minimum-scale="+SCALE+", maximum-scale=5, shrink-to-fit=no";}
function makeMql(media,matches){
	return{media:media,matches:matches,onchange:null,addListener:function(){},removeListener:function(){},addEventListener:function(){},removeEventListener:function(){},dispatchEvent:function(){return true;}};
}
function patchViewport(){
	var c=viewportContent();
	var nodes=document.querySelectorAll('meta[name="viewport"]');
	if(!nodes.length){
		var m=document.createElement("meta");
		m.name="viewport";
		m.content=c;
		(document.head||document.documentElement).appendChild(m);
		return;
	}
	for(var i=0;i<nodes.length;i++){nodes[i].setAttribute("content",c);}
}
function patchNavigator(){
	try{
		Object.defineProperty(navigator,"userAgent",{get:function(){return UA;},configurable:true});
		Object.defineProperty(navigator,"platform",{get:function(){return"Win32";},configurable:true});
		Object.defineProperty(navigator,"maxTouchPoints",{get:function(){return 0;},configurable:true});
		Object.defineProperty(navigator,"vendor",{get:function(){return"Google Inc.";},configurable:true});
	}catch(e){}
}
function patchWindowDimensions(){
	try{
		Object.defineProperty(window,"innerWidth",{get:function(){return W;},configurable:true});
		Object.defineProperty(window,"outerWidth",{get:function(){return W;},configurable:true});
		Object.defineProperty(window,"innerHeight",{get:function(){return ${DESKTOP_EMBED_LAYOUT_HEIGHT};},configurable:true});
	}catch(e){}
}
function patchScreen(){
	try{
		Object.defineProperty(screen,"width",{get:function(){return W;},configurable:true});
		Object.defineProperty(screen,"height",{get:function(){return ${DESKTOP_EMBED_LAYOUT_HEIGHT};},configurable:true});
		Object.defineProperty(screen,"availWidth",{get:function(){return W;},configurable:true});
	}catch(e){}
}
function patchDocumentElement(){
	var el=document.documentElement;
	try{
		Object.defineProperty(el,"clientWidth",{get:function(){return W;},configurable:true});
		Object.defineProperty(el,"scrollWidth",{get:function(){return W;},configurable:true});
		Object.defineProperty(el,"offsetWidth",{get:function(){return W;},configurable:true});
	}catch(e){}
}
function patchTouchSignals(){
	try{delete window.ontouchstart;}catch(e){}
	window.ontouchstart=undefined;
}
function patchMatchMedia(){
	var nativeMatchMedia=window.matchMedia.bind(window);
	window.matchMedia=function(q){
		var query=String(q||"");
		if(/\\(hover:\\s*hover\\)/i.test(query))return makeMql(query,true);
		if(/\\(hover:\\s*none\\)/i.test(query))return makeMql(query,false);
		if(/\\(any-hover:\\s*hover\\)/i.test(query))return makeMql(query,true);
		if(/\\(any-hover:\\s*none\\)/i.test(query))return makeMql(query,false);
		if(/\\(pointer:\\s*fine\\)/i.test(query))return makeMql(query,true);
		if(/\\(pointer:\\s*coarse\\)/i.test(query))return makeMql(query,false);
		if(/\\(any-pointer:\\s*fine\\)/i.test(query))return makeMql(query,true);
		if(/\\(any-pointer:\\s*coarse\\)/i.test(query))return makeMql(query,false);
		if(/\\(orientation:\\s*landscape\\)/i.test(query))return makeMql(query,true);
		if(/\\(orientation:\\s*portrait\\)/i.test(query))return makeMql(query,false);
		var maxM=query.match(/\\(max-width:\\s*(\\d+(?:\\.\\d+)?)px\\)/i);
		if(maxM)return makeMql(query,W<=parseFloat(maxM[1]));
		var minM=query.match(/\\(min-width:\\s*(\\d+(?:\\.\\d+)?)px\\)/i);
		if(minM)return makeMql(query,W>=parseFloat(minM[1]));
		var maxDM=query.match(/\\(max-device-width:\\s*(\\d+(?:\\.\\d+)?)px\\)/i);
		if(maxDM)return makeMql(query,W<=parseFloat(maxDM[1]));
		var minDM=query.match(/\\(min-device-width:\\s*(\\d+(?:\\.\\d+)?)px\\)/i);
		if(minDM)return makeMql(query,W>=parseFloat(minDM[1]));
		return nativeMatchMedia(q);
	};
}
function boot(){
	patchViewport();
	patchNavigator();
	patchWindowDimensions();
	patchScreen();
	patchDocumentElement();
	patchTouchSignals();
	patchMatchMedia();
}
boot();
document.addEventListener("DOMContentLoaded",boot);
window.addEventListener("load",boot);
window.addEventListener("pageshow",boot);
if(typeof MutationObserver!=="undefined"){
	new MutationObserver(function(){patchViewport();}).observe(document.documentElement,{childList:true,subtree:true});
}
})();`;
}

function buildDesktopEmbedViewportMeta(fitWidthPx?: number): string {
	const scale = computeDesktopEmbedScale(fitWidthPx);
	const content =
		scale < 1
			? `width=${DESKTOP_EMBED_LAYOUT_WIDTH}, initial-scale=${scale}, minimum-scale=${scale}, maximum-scale=5, shrink-to-fit=no`
			: `width=${DESKTOP_EMBED_LAYOUT_WIDTH}, initial-scale=1, maximum-scale=5, shrink-to-fit=no`;
	return `<meta name="viewport" content="${content}">`;
}

function buildDesktopEmbedPatchStyles(): string {
	return `<style id="techxeed-desktop-embed">${DESKTOP_EMBED_PATCH_CSS}</style>`;
}

/** CSS injected into proxied demo pages to force desktop nav/layout patterns. */
export const DESKTOP_EMBED_PATCH_CSS =
	"html,body{opacity:1!important;visibility:visible!important}" +
	"#page-loader-pro,.page-loader-pro,.site-preloader,#preloader{display:none!important;opacity:0!important;visibility:hidden!important;pointer-events:none!important}" +
	"#boxed-layout-pro{opacity:1!important;visibility:visible!important}" +
	".navbar-toggler,.menu-toggle,.mobile-menu-button,.hamburger-menu,.mobile-nav-toggle,.header__burger,.elementor-menu-toggle{display:none!important}" +
	".navbar-collapse.collapse:not(.show),.mobile-nav{display:flex!important;flex-basis:auto!important;height:auto!important;visibility:visible!important}" +
	"@media (max-width:991.98px){.d-lg-none,.d-xl-none,.visible-xs,.visible-sm,.visible-md,.mobile-only{display:none!important}" +
	".d-none.d-lg-block,.d-none.d-lg-flex,.d-none.d-lg-inline-block,.d-none.d-xl-block,.d-none.d-xl-flex,.hidden-xs,.hidden-sm,.hidden-md{display:revert!important}" +
	".navbar-expand-lg .navbar-nav,.navbar-expand-md .navbar-nav{flex-direction:row!important}" +
	".navbar-expand-lg .navbar-collapse,.navbar-expand-md .navbar-collapse{display:flex!important}" +
	".navbar-expand-lg .navbar-toggler,.navbar-expand-md .navbar-toggler{display:none!important}}";

/** Re-apply desktop embed overrides after theme scripts run (same-origin iframe only). */
export function reinforceDesktopEmbedDocument(doc: Document): void {
	const w = DESKTOP_EMBED_LAYOUT_WIDTH;
	const viewportContent = `width=${w}, initial-scale=1, maximum-scale=5, shrink-to-fit=no`;
	let meta = doc.querySelector('meta[name="viewport"]');
	if (!meta) {
		meta = doc.createElement("meta");
		meta.setAttribute("name", "viewport");
		doc.head?.prepend(meta);
	}
	meta.setAttribute("content", viewportContent);
	if (!doc.getElementById("techxeed-desktop-embed")) {
		const style = doc.createElement("style");
		style.id = "techxeed-desktop-embed";
		style.textContent = DESKTOP_EMBED_PATCH_CSS;
		doc.head?.appendChild(style);
	}
}

/** Desktop preview bootstrap: viewport meta first (Safari), then CSS + script. */
export function injectDesktopEmbedHead(html: string, initialScale: number, fitWidthPx?: number): string {
	const bundle = `${buildDesktopEmbedViewportMeta(fitWidthPx)}${buildDesktopEmbedPatchStyles()}<script>${getDesktopEmbedForcedLayoutScript(initialScale)}<\/script>`;
	const withoutViewport = html.replace(/<meta[^>]+name=["']viewport["'][^>]*>/gi, "");
	if (/<head(\s[^>]*)?>/i.test(withoutViewport)) {
		return withoutViewport.replace(/<head(\s[^>]*)?>/i, (m) => `${m}${bundle}`);
	}
	if (/<html(\s[^>]*)?>/i.test(withoutViewport)) {
		return withoutViewport.replace(/<html(\s[^>]*)?>/i, (m) => `${m}<head>${bundle}</head>`);
	}
	return `<!DOCTYPE html><html><head>${bundle}</head><body>${withoutViewport}</body></html>`;
}

export function injectDesktopEmbedViewport(html: string, fitWidthPx?: number): string {
	const meta = buildDesktopEmbedViewportMeta(fitWidthPx);
	const withoutViewport = html.replace(/<meta[^>]+name=["']viewport["'][^>]*>/gi, "");
	if (/<head(\s[^>]*)?>/i.test(withoutViewport)) {
		return withoutViewport.replace(/<head(\s[^>]*)?>/i, (m) => `${m}${meta}`);
	}
	return `<!DOCTYPE html><html><head>${meta}</head><body>${withoutViewport}</body></html>`;
}

export function injectMobileEmbedViewport(html: string): string {
	const meta =
		`<meta name="viewport" content="width=${MOBILE_EMBED_LAYOUT_WIDTH}, initial-scale=1, maximum-scale=5, viewport-fit=cover">`;
	if (/<meta[^>]+name=["']viewport["']/i.test(html)) {
		return html.replace(/<meta[^>]+name=["']viewport["'][^>]*>/i, meta);
	}
	if (/<head(\s[^>]*)?>/i.test(html)) {
		return html.replace(/<head(\s[^>]*)?>/i, (m) => `${m}${meta}`);
	}
	return `<!DOCTYPE html><html><head>${meta}</head><body>${html}</body></html>`;
}

export function prepareDemoEmbedHtml(
	html: string,
	pageUrl: URL,
	allowedOrigin: string,
	options?: {
		viewport?: DemoEmbedViewportMode;
		fitWidth?: number;
		layout?: typeof DEMO_EMBED_LAYOUT_FRAME_VALUE | typeof DEMO_EMBED_LAYOUT_META_VALUE;
	},
): string {
	const withoutBase = stripHtmlBaseTags(html);
	const withAbsoluteAssets = absolutizeDemoRelativeUrls(withoutBase, pageUrl);
	let prepared = injectDemoEmbedGuard(withAbsoluteAssets, allowedOrigin, getDemoEmbedBaseHref(pageUrl));
	if (options?.viewport === "mobile") {
		prepared = injectMobileEmbedViewport(prepared);
	} else {
		const layout = options?.layout ?? DEMO_EMBED_LAYOUT_FRAME_VALUE;
		const fitWidth =
			layout === DEMO_EMBED_LAYOUT_META_VALUE ? options?.fitWidth : undefined;
		const scale = computeDesktopEmbedScale(fitWidth);
		prepared = injectDesktopEmbedHead(prepared, scale, fitWidth);
	}
	return prepared;
}
